import { useEffect, useState } from "react";
import { isMobileViewport } from "@/lib/utils";
import { NavLink, useNavigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Users, FolderKanban, Truck, TrendingDown,
  Receipt, Wallet, BarChart3, Settings as SettingsIcon, LogOut, Menu, X,
  LifeBuoy, CreditCard, FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import ErrorBoundary from "@/components/ErrorBoundary";
import QuickCreateFab from "@/components/QuickCreateFab";

const NAV = [
  { to: "/", label: "Gösterge Paneli", icon: LayoutDashboard, testId: "nav-dashboard", end: true },
  { to: "/customers", label: "Müşteriler", icon: Users, testId: "nav-customers" },
  { to: "/jobs", label: "İşler / Satışlar", icon: FolderKanban, testId: "nav-jobs" },
  { to: "/vendors", label: "Tedarikçiler", icon: Truck, testId: "nav-vendors" },
  { to: "/purchases", label: "Satın Almalar", icon: TrendingDown, testId: "nav-purchases" },
  { to: "/invoices", label: "Faturalar", icon: FileText, testId: "nav-invoices" },
  { to: "/expenses", label: "Giderler", icon: Receipt, testId: "nav-expenses" },
  { to: "/cash", label: "Kasalar", icon: Wallet, testId: "nav-cash" },
  { to: "/reports", label: "Raporlar", icon: BarChart3, testId: "nav-reports" },
  { to: "/support", label: "Öneri / Destek", icon: LifeBuoy, testId: "nav-support" },
  { to: "/settings", label: "Ayarlar", icon: SettingsIcon, testId: "nav-settings" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Mobilde her route değişiminde sayfa en üstten açılır (desktop davranışı aynı).
  useEffect(() => {
    if (isMobileViewport()) window.scrollTo(0, 0);
  }, [location.pathname]);
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const SidebarContent = (
    <div className="flex flex-col h-full">
      <div className="px-6 pt-7 pb-6 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <img
            src="/penz-icon.png"
            alt="PENZ"
            className="w-8 h-8 rounded-lg object-contain"
            data-testid="sidebar-brand-icon"
          />
          <div>
            <img
              src="/penz-logo.png"
              alt="PENZ"
              className="h-5 w-auto object-contain"
              data-testid="sidebar-brand-logo"
            />
            <div className="text-[11px] text-slate-500 uppercase tracking-[0.18em] mt-1">Yönetim Paneli</div>
          </div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {NAV.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testId}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-150 ${
                  isActive
                    ? "bg-slate-900 text-white shadow-sm"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              <span className="font-medium">{item.label}</span>
            </NavLink>
          );
        })}
        {/* Trial kullanıcılara ve trial-expired'a "Aboneliği Yükselt"
            görünür; paid ve internal_free için gizli tutulur. */}
        {user && ["trial", "trial_expired"].includes(user.subscription_status) && (
          <NavLink
            to="/abonelik"
            data-testid="nav-subscription"
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-150 mt-3 border ${
                isActive
                  ? "bg-slate-900 text-white shadow-sm border-slate-900"
                  : "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100"
              }`
            }
          >
            <CreditCard className="w-4 h-4" />
            <span className="font-medium">Aboneliği Yükselt</span>
          </NavLink>
        )}
      </nav>

      <div className="px-3 py-4 border-t border-slate-200 space-y-2">
        <div className="px-3 py-2 text-xs text-slate-500">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Oturum</div>
          <div className="text-slate-700 font-medium mt-0.5" data-testid="current-username">
            {user?.username}
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="w-full justify-start gap-2 rounded-lg border-slate-200"
          onClick={handleLogout}
          data-testid="logout-button"
        >
          <LogOut className="w-4 h-4" /> Çıkış Yap
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="hidden md:block fixed left-0 top-0 w-64 h-screen bg-white border-r border-slate-200">
        {SidebarContent}
      </aside>

      {/* Mobile sidebar */}
      {open && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="w-64 bg-white border-r border-slate-200">{SidebarContent}</div>
          <div className="flex-1 bg-slate-900/40" onClick={() => setOpen(false)} />
        </div>
      )}

      {/* Mobile topbar */}
      <header className="md:hidden sticky top-0 z-30 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => setOpen(true)}
          className="p-2 -ml-2 rounded-lg hover:bg-slate-100"
          data-testid="mobile-menu-button"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
        <img
          src="/penz-logo.png"
          alt="PENZ"
          className="h-5 w-auto object-contain"
          data-testid="mobile-brand-logo"
        />
        <div className="w-9" />
      </header>

      {/* Main */}
      <main className="md:ml-64 p-4 md:p-8 min-h-screen">
        {/* Per-route ErrorBoundary keyed by pathname so navigating to another
            route automatically resets the error state. */}
        <ErrorBoundary key={location.pathname}>
          <Outlet />
        </ErrorBoundary>
      </main>

      {/* Global hızlı erişim FAB'ı — operasyonel ana sayfalarda görünür.
          Ayarlar (/settings) ve Öneri/Destek (/support) ekranlarında
          gizlenir; ayrıca alt-yol'ları da (örn /settings/security)
          gizlenir.  Diğer tüm sayfalarda (Dashboard, Müşteriler, İşler,
          Tedarikçiler, Satın Almalar, Faturalar, Giderler, Kasalar,
          Raporlar ve detay sayfaları) sağ altta sabit durur. */}
      {(() => {
        const hidden = ["/settings", "/support"];
        const isHidden = hidden.some((p) =>
          location.pathname === p || location.pathname.startsWith(p + "/")
        );
        return isHidden ? null : (
          <div className="hidden md:block" data-testid="quick-create-fab-desktop-wrapper"><QuickCreateFab /></div>
        );
      })()}
    </div>
  );
}
